import { useId, useState } from "react";
import type {
  ComparedDocumentResponse,
  MissingDocumentResponse,
  PositionAnalysisResponse,
  PositionResponse,
} from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { FieldLabel } from "@/components/ui/FieldLabel";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Note, NotSavedNote } from "@/components/ui/Note";
import { refusalDetail } from "@/services/api-client";
import { useUnsavedDraft } from "@/hooks/use-unsaved-draft";
import { Panel } from "@/components/ui/Panel";
import { Textarea } from "@/components/ui/textarea";
import { GAP_REASON_LABELS } from "@/lib/gap-reason-labels";
import { warnsBeforeConfirming } from "./ConfirmAgainstAdvice";

/** Goes on without the documents a position was not compared with, and why; resolves once the server has taken it. */
export type AcknowledgeGaps = (
  positionId: string,
  fileIds: string[],
  reason: string,
) => Promise<unknown>;

export function acknowledge(
  position: PositionAnalysisResponse,
  reason: string,
  onAcknowledgeGaps: AcknowledgeGaps,
): Promise<unknown> {
  return onAcknowledgeGaps(
    position.position_id,
    position.not_assessed_against.map((d) => d.file_id),
    reason,
  );
}

/** The caption over why the attorney goes on without the missing documents, in their number. */
function reasonLabel(count: number): string {
  return count === 1
    ? "Reason for confirming without it"
    : "Reason for confirming without them";
}

/** A document as the library names it: its reference, then its file. */
function nameOf(document: ComparedDocumentResponse): string {
  return document.reference_id === null
    ? document.source_file
    : `${document.reference_id} (${document.source_file})`;
}

function plural(count: number, one: string): string {
  return `${count} ${one}${count === 1 ? "" : "s"}`;
}

/** The documents the verdict rests on, so what it was not compared with reads against what it was. */
export function ComparedWith({
  documents,
}: {
  documents: readonly ComparedDocumentResponse[];
}) {
  if (documents.length === 0) return null;
  return (
    <p
      data-testid="compared-with"
      className="text-[12px] text-muted-foreground"
    >
      Compared with {documents.map(nameOf).join(", ")}.
    </p>
  );
}

/** Each missing document and why, one to a line. */
function MissingList({
  documents,
}: {
  documents: readonly MissingDocumentResponse[];
}) {
  return (
    <ul className="mt-1 list-disc pl-5">
      {documents.map((document) => (
        <li key={document.file_id}>
          <b className="font-mono">{nameOf(document)}</b>:{" "}
          {GAP_REASON_LABELS[document.reason]}
        </li>
      ))}
    </ul>
  );
}

/**
 * The one prompt before confirming a position without what it was not compared
 * with: on a yes, the yes and the acknowledgement together, and the advice
 * warning in the same words when the verdict is adverse, so the attorney
 * answers once. Going on without a document overrides the gap, so it asks
 * why, and the yes stays closed until the reason has words.
 */
export function ConfirmWithout({
  position,
  confirming,
  onCancel,
  onGoOn,
}: {
  position: PositionAnalysisResponse;
  confirming: boolean;
  onCancel: () => void;
  /** Settles with the server's answer; the caller closes the prompt once it is taken. */
  onGoOn: (reason: string) => Promise<unknown>;
}) {
  const reasonId = useId();
  const [reason, setReason] = useState("");
  // Why the server last refused, in its words: said here, since the prompt stays over the row.
  const [refused, setRefused] = useState<string | null>(null);
  useUnsavedDraft(refused !== null);
  const goOn = () => {
    setRefused(null);
    onGoOn(reason.trim()).catch((error: unknown) => {
      setRefused(refusalDetail(error, "The server did not take it."));
    });
  };
  const count = plural(position.not_assessed_against.length, "document");
  const adverse = confirming && warnsBeforeConfirming(position.verdict);
  return (
    <ConfirmDialog
      open
      title={`Confirm without comparing ${count}?`}
      description={
        <>
          This verdict rests only on the documents it was compared with. It was
          not compared with{" "}
          {position.not_assessed_against
            .map((d) => `${nameOf(d)}: ${GAP_REASON_LABELS[d.reason]}`)
            .join("; ")}
          .{" "}
          {position.not_assessed_against.length === 1
            ? "This document may disclose the position"
            : "One of these documents may disclose the position"}
          , and assessing it again may close the gap.
          {adverse &&
            " The prior art already reaches this position, so confirming it as it stands may not yield a novel claim."}
        </>
      }
      actionLabel="Confirm"
      actionDisabled={reason.trim() === ""}
      holdsOpen
      onCancel={onCancel}
      onConfirm={goOn}
    >
      <div>
        <FieldLabel htmlFor={reasonId} required>
          {reasonLabel(position.not_assessed_against.length)}
        </FieldLabel>
        <Textarea
          id={reasonId}
          rows={3}
          aria-required
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder={
            position.not_assessed_against.length === 1
              ? "Why can this position stand without this document? Note what you checked in its place."
              : "Why can this position stand without these documents? Note what you checked in their place."
          }
        />
        {refused !== null && (
          <Note tone="destructive" className="mt-1">
            {refused}
          </Note>
        )}
        <NotSavedNote failed={refused !== null} onRetry={goOn} />
      </div>
    </ConfirmDialog>
  );
}

/** Whether the attorney already went on without the documents and why, and why the server last refused to. */
function WhereItStands({
  acknowledged,
  reason,
  count,
  refusal,
}: {
  acknowledged: boolean;
  reason: string | null;
  count: number;
  refusal: string | null;
}) {
  return (
    <>
      {acknowledged && (
        <p data-testid="gaps-acknowledged" className="mt-1">
          {count === 1
            ? "Confirmed without this document."
            : "Confirmed without these documents."}
        </p>
      )}
      {acknowledged && reason !== null && (
        <div className="mt-1">
          <MicroLabel as="p" size="sm">
            {reasonLabel(count)}
          </MicroLabel>
          <p>{reason}</p>
        </div>
      )}
      {refusal !== null && (
        <p role="alert" className="mt-1 font-semibold text-destructive">
          It could not be confirmed without them: {refusal}
        </p>
      )}
    </>
  );
}

/**
 * The acknowledgement alone, for a position already confirmed whose gaps hold
 * it: the act, and the prompt that asks why before it goes on.
 */
function AcknowledgeAlone({
  position,
  busy,
  onAcknowledgeGaps,
}: {
  position: PositionAnalysisResponse;
  busy: boolean;
  onAcknowledgeGaps: AcknowledgeGaps;
}) {
  const [asking, setAsking] = useState(false);
  return (
    <>
      <Button
        type="button"
        size="dense"
        variant="quiet"
        disabled={busy}
        onClick={() => setAsking(true)}
        className="cursor-pointer"
      >
        Confirm
      </Button>
      {asking && (
        <ConfirmWithout
          position={position}
          confirming={false}
          onCancel={() => setAsking(false)}
          onGoOn={(reason) =>
            acknowledge(position, reason, onAcknowledgeGaps).then(() => {
              setAsking(false);
            })
          }
        />
      )}
    </>
  );
}

/**
 * What the verdict was not compared with, and why, drawn under the head so it
 * is seen with the row shut. Assessing again asks only what is missing. A
 * position already confirmed whose gaps hold it is offered the acknowledgement
 * alone; one not yet confirmed gets it with the yes.
 */
export function MissingComparisons({
  position,
  attorney,
  busy,
  assessing,
  onAssess,
  onAcknowledgeGaps,
  refusal,
}: {
  position: PositionAnalysisResponse;
  attorney: PositionResponse | undefined;
  busy: boolean;
  assessing: boolean;
  onAssess: (positionId: string) => void;
  onAcknowledgeGaps: AcknowledgeGaps;
  /** Why the server would not confirm the position without these documents, or nothing. */
  refusal: string | null;
}) {
  const missing = position.not_assessed_against;
  if (missing.length === 0) return null;
  const total = missing.length + position.assessed_against.length;
  const acknowledgeAlone = position.gaps_hold && attorney?.confirmed === true;
  return (
    <Panel
      data-testid="missing-comparisons"
      shape="row"
      pad="tight"
      tone="warning"
      // Inside the row's own margin, under its head.
      className="mx-[15px] mb-[13px] text-[11.5px]"
    >
      <p className="font-semibold">
        Not compared with {missing.length} of{" "}
        {plural(total, "readable document")}:
      </p>
      <MissingList documents={missing} />
      <WhereItStands
        acknowledged={position.gaps_acknowledged}
        reason={position.gaps_acknowledged_reason}
        count={missing.length}
        refusal={refusal}
      />
      <div className="mt-2 flex flex-wrap gap-2">
        <Button
          type="button"
          size="dense"
          variant="secondary"
          disabled={busy || assessing}
          onClick={() => onAssess(position.position_id)}
          className="cursor-pointer"
        >
          {assessing ? "Assessing…" : "Assess again"}
        </Button>
        {acknowledgeAlone && (
          <AcknowledgeAlone
            position={position}
            busy={busy}
            onAcknowledgeGaps={onAcknowledgeGaps}
          />
        )}
      </div>
    </Panel>
  );
}
